/**
 * DirectSourcePanel.jsx
 *
 * Relay Source node — Direct Connection mode.
 * Lets the user:
 *   1. Pick a datasource connection
 *   2. Browse its tables and columns (via /datasources/{id}/schema)
 *   3. For each column: assign a HIPAA PD identifier + action (same as the Data Model PD editor)
 *   4. Tables with ≥1 PD column → Relay Agent; others → DuckDB fast path
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Database, Table2, RefreshCw, ChevronDown, ChevronRight,
    AlertCircle, Shield
} from 'lucide-react';
import { apiGet } from '@/api';

// ─── Constants (mirrors DataModelWizard step 4) ────────────────────────────

const DB_TYPES = new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 13]);
const DB_TYPE_LABELS = {
    0: 'PostgreSQL', 1: 'MySQL', 2: 'SQL Server', 3: 'Oracle',
    4: 'Snowflake', 5: 'BigQuery', 6: 'Redshift', 7: 'Azure SQL',
    8: 'Synapse', 13: 'DuckDB',
};

const DATE_ID_KEYS = new Set(['DOB', 'AGE_OVER_89']);

const HIPAA_IDENTIFIERS = [
    { key: 'NAME',         label: 'Name' },
    { key: 'ADDRESS',      label: 'Address' },
    { key: 'DOB',          label: 'Date of Birth' },
    { key: 'PHONE',        label: 'Phone / Fax' },
    { key: 'EMAIL',        label: 'Email' },
    { key: 'SSN',          label: 'SSN' },
    { key: 'MRN',          label: 'MRN' },
    { key: 'ACCOUNT',      label: 'Account #' },
    { key: 'LICENSE',      label: 'License #' },
    { key: 'IP',           label: 'IP Address' },
    { key: 'UID',          label: 'Device / UID' },
    { key: 'AGE_OVER_89',  label: 'Age > 89' },
    { key: 'BIOMETRIC',    label: 'Biometric' },
    { key: 'PHOTO',        label: 'Photo' },
    { key: 'URL',          label: 'URL' },
    { key: 'VEHICLE',      label: 'Vehicle ID' },
    { key: 'BENEFICIARY',  label: 'Beneficiary #' },
    { key: 'DEVICE',       label: 'Device ID' },
    { key: 'EXPLICIT_PHI', label: 'Explicit PHI' },
];

const STANDARD_ACTIONS = [
    { value: 'mask',        label: 'Mask' },
    { value: 'hash',        label: 'Hash (SHA-256)' },
    { value: 'suppress',    label: 'Suppress (null)' },
    { value: 'passthrough', label: 'Pass Through' },
];

const DATE_ACTIONS = [
    { value: 'date_year_only',  label: 'Year Only  →  1990' },
    { value: 'date_month_only', label: 'Month Only  →  March' },
    { value: 'date_year_month', label: 'Year + Month  →  1990-03' },
    { value: 'date_quarter',    label: 'Quarter  →  Q1 1990' },
    { value: 'date_age_range',  label: 'Age Range (5-yr)  →  30-34' },
    { value: 'date_shift',      label: 'Date Shift (±30 days)' },
    { value: 'suppress',        label: 'Suppress (null)' },
    { value: 'passthrough',     label: 'Pass Through' },
];

const MASK_PATTERNS = [
    { value: '*',               label: '* Asterisk  →  J***h' },
    { value: '#',               label: '# Hash  →  J###h' },
    { value: 'X',               label: 'X Letter  →  JXXXh' },
    { value: '[REDACTED]',      label: '[REDACTED] label' },
    { value: 'partial_start',   label: 'Partial (keep start)  →  Jo***' },
    { value: 'partial_end',     label: 'Partial (keep end)  →  ***hn' },
    { value: 'format_preserve', label: 'Format-preserving' },
];

const DEFAULT_ACTIONS = {
    NAME: 'mask', ADDRESS: 'mask', DOB: 'date_year_only',
    PHONE: 'mask', EMAIL: 'mask', SSN: 'suppress', MRN: 'hash',
    ACCOUNT: 'hash', LICENSE: 'hash', IP: 'hash', UID: 'hash',
    AGE_OVER_89: 'date_age_range', BIOMETRIC: 'suppress', PHOTO: 'suppress',
    URL: 'suppress', VEHICLE: 'suppress', BENEFICIARY: 'hash',
    DEVICE: 'suppress', EXPLICIT_PHI: 'suppress',
};

// ─── Column Row ───────────────────────────────────────────────────────────────

function ColRow({ col, mapping, onChange }) {
    const idKey    = mapping?.identifier || '';
    const action   = mapping?.action     || (idKey ? (DEFAULT_ACTIONS[idKey] || 'mask') : '');
    const maskChar = mapping?.mask_char  || '*';
    const isDate   = DATE_ID_KEYS.has(idKey);
    const isMask   = action === 'mask';

    const actionList = isDate ? DATE_ACTIONS : STANDARD_ACTIONS;

    // Action cell background color
    const actionCls = !idKey ? 'text-gray-300 bg-white border-gray-100'
        : action === 'mask'          ? 'text-amber-700  bg-amber-50   border-amber-200'
        : action === 'hash'          ? 'text-sky-700    bg-sky-50     border-sky-200'
        : action === 'suppress'      ? 'text-red-700    bg-red-50     border-red-200'
        : action.startsWith('date_') ? 'text-violet-700 bg-violet-50  border-violet-200'
        : 'text-gray-600 bg-gray-50 border-gray-200';

    return (
        <tr className={`border-b border-gray-50 last:border-0 ${idKey ? 'bg-red-50/20' : 'hover:bg-gray-50'}`}>
            {/* Column name + data type */}
            <td className="px-3 py-2 align-top">
                <span className={`text-[11px] font-mono ${idKey ? 'text-red-800 font-semibold' : 'text-slate-700'}`}>
                    {col.name}
                </span>
                <span className="ml-1.5 text-[9px] text-gray-400 bg-gray-100 px-1 rounded">
                    {col.dataType || col.data_type || ''}
                </span>
            </td>

            {/* Identifier (HIPAA key) */}
            <td className="px-3 py-2 align-top w-[130px]">
                <select
                    value={idKey}
                    onChange={e => {
                        const newKey = e.target.value;
                        const newAction = newKey ? (DEFAULT_ACTIONS[newKey] || 'mask') : '';
                        onChange({ identifier: newKey, action: newAction, mask_char: '*' });
                    }}
                    className="w-full text-[10px] px-1.5 py-1 border border-gray-200 rounded bg-white text-gray-700 focus:outline-none focus:border-blue-400"
                >
                    <option value="">— None —</option>
                    {HIPAA_IDENTIFIERS.map(id => (
                        <option key={id.key} value={id.key}>{id.label}</option>
                    ))}
                </select>
            </td>

            {/* Action + pattern */}
            <td className="px-3 py-2 align-top">
                <select
                    value={action}
                    disabled={!idKey}
                    onChange={e => onChange({ ...mapping, identifier: idKey, action: e.target.value, mask_char: maskChar })}
                    className={`w-full text-[10px] px-1.5 py-1 border rounded font-semibold ${actionCls} disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none`}
                >
                    {actionList.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>

                {/* Mask pattern sub-option — same as DataModelWizard */}
                {isMask && idKey && (
                    <select
                        value={maskChar}
                        onChange={e => onChange({ ...mapping, identifier: idKey, action, mask_char: e.target.value })}
                        className="mt-1 w-full text-[9px] px-1.5 py-1 border border-amber-200 rounded bg-amber-50/70 text-amber-800 font-medium focus:outline-none"
                    >
                        {MASK_PATTERNS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                )}

                {action.startsWith('date_') && idKey && (
                    <span className="text-[9px] text-violet-500 pl-0.5 block mt-0.5">date analytics transform ✦</span>
                )}
            </td>
        </tr>
    );
}

// ─── Table Accordion ─────────────────────────────────────────────────────────

function TableAccordion({ table, included, colMappings, onToggle, onColChange }) {
    const [open, setOpen] = useState(false);

    const pdCount  = Object.values(colMappings || {}).filter(m => m?.identifier).length;
    const hasPd    = pdCount > 0;
    const colCount = (table.columns || []).length;

    return (
        <div className={`border rounded-lg overflow-hidden ${
            included
                ? hasPd
                    ? 'border-red-200'
                    : 'border-green-200'
                : 'border-gray-100 opacity-50'
        }`}>
            {/* Header row */}
            <div
                className={`flex items-center gap-2 px-3 py-2 select-none cursor-pointer ${
                    included ? (hasPd ? 'bg-red-50' : 'bg-green-50') : 'bg-gray-50'
                }`}
                onClick={() => included && setOpen(v => !v)}
            >
                <input
                    type="checkbox"
                    checked={included}
                    className="accent-blue-600 shrink-0"
                    onClick={e => e.stopPropagation()}
                    onChange={e => { e.stopPropagation(); onToggle(e.target.checked); }}
                />

                <Table2 className={`h-3.5 w-3.5 shrink-0 ${hasPd ? 'text-red-500' : 'text-green-500'}`} />

                <span className="text-[11px] font-semibold text-gray-800 flex-1 truncate">{table.name}</span>

                <span className="text-[9px] text-gray-400">{colCount} cols</span>

                {included && hasPd && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 flex items-center gap-0.5">
                        <Shield size={8} /> {pdCount} PD
                    </span>
                )}

                {included && (
                    open
                        ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                        : <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                )}
            </div>

            {/* Column table */}
            {included && open && (
                <div className="border-t border-gray-100">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Column</th>
                                <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[130px]">PD Identifier</th>
                                <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Action / Pattern</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(table.columns || []).map(col => (
                                <ColRow
                                    key={col.name}
                                    col={col}
                                    mapping={(colMappings || {})[col.name]}
                                    onChange={m => onColChange(col.name, m)}
                                />
                            ))}
                        </tbody>
                    </table>
                    {/* Legend */}
                    <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-100 flex flex-wrap gap-3 text-[9px] text-gray-500">
                        <span><span className="text-amber-600 font-bold">■</span> Mask</span>
                        <span><span className="text-sky-600 font-bold">■</span> Hash</span>
                        <span><span className="text-red-600 font-bold">■</span> Suppress</span>
                        <span><span className="text-violet-600 font-bold">■</span> Date transform</span>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * Props:
 *   directConfig  — node.data.directConfig: {
 *                     datasourceId: string,
 *                     tables: {
 *                       [tableName]: { included: boolean, columns: { [colName]: { identifier, action, mask_char } } }
 *                     }
 *                   }
 *   onChange      — (directConfig) => void
 */
export default function DirectSourcePanel({ directConfig = {}, onChange }) {
    const [dataSources,   setDataSources]   = useState([]);
    const [dsLoading,     setDsLoading]     = useState(false);
    const [dsError,       setDsError]       = useState(null);
    const [schemaLoading, setSchemaLoading] = useState(false);
    const [schemaError,   setSchemaError]   = useState(null);
    const [tables,        setTables]        = useState([]);   // raw schema from API

    const selectedDs  = directConfig.datasourceId || '';
    const tableConfig = directConfig.tables        || {};

    // ── Load datasource list ─────────────────────────────────────────────────
    const loadDs = useCallback(() => {
        setDsLoading(true); setDsError(null);
        apiGet('/datasources')
            .then(res => {
                if (Array.isArray(res)) {
                    setDataSources(res.filter(d => DB_TYPES.has(d.type)));
                }
            })
            .catch(() => setDsError('Could not load datasources'))
            .finally(() => setDsLoading(false));
    }, []);

    useEffect(() => { loadDs(); }, [loadDs]);

    // ── Load schema when datasource changes ──────────────────────────────────
    const loadSchema = useCallback((dsId) => {
        if (!dsId) return;
        setSchemaLoading(true); setSchemaError(null); setTables([]);

        // Use /structure which returns { tables: [{ name, columns: [{ name, dataType }] }] }
        apiGet(`/datasources/${dsId}/structure`)
            .then(res => {
                const rawTables = Array.isArray(res)
                    ? res
                    : Array.isArray(res?.tables)
                        ? res.tables
                        : [];

                if (rawTables.length === 0) {
                    setSchemaError('No tables found in this datasource.');
                    return;
                }

                setTables(rawTables);

                // Pre-populate tableConfig with new tables (include by default, empty columns)
                const existing = directConfig.tables || {};
                const additions = {};
                rawTables.forEach(t => {
                    if (!(t.name in existing)) {
                        additions[t.name] = { included: true, columns: {} };
                    }
                });
                if (Object.keys(additions).length > 0) {
                    onChange({ ...directConfig, datasourceId: dsId, tables: { ...additions, ...existing } });
                }
            })
            .catch(() => setSchemaError('Could not load schema. Check the datasource connection.'))
            .finally(() => setSchemaLoading(false));
    }, [directConfig, onChange]);

    useEffect(() => {
        if (selectedDs) loadSchema(selectedDs);
    }, [selectedDs]);

    // ── Handlers ─────────────────────────────────────────────────────────────

    const handleDsChange = (dsId) => {
        onChange({ datasourceId: dsId, tables: {} });
        setTables([]);
        setSchemaError(null);
    };

    const handleToggle = (tableName, included) => {
        onChange({
            ...directConfig,
            tables: { ...tableConfig, [tableName]: { ...(tableConfig[tableName] || {}), included } },
        });
    };

    const handleColChange = (tableName, colName, m) => {
        const tbl = tableConfig[tableName] || { included: true, columns: {} };
        onChange({
            ...directConfig,
            tables: {
                ...tableConfig,
                [tableName]: { ...tbl, columns: { ...(tbl.columns || {}), [colName]: m } },
            },
        });
    };

    // Derived counts
    const includedTables = tables.filter(t => tableConfig[t.name]?.included !== false);
    const pdTables       = includedTables.filter(t =>
        Object.values(tableConfig[t.name]?.columns || {}).some(m => m?.identifier)
    );

    return (
        <div className="space-y-4">
            {/* ── Datasource selector ────────────────────────────────── */}
            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                    <Database className="h-3.5 w-3.5 text-green-500" /> Connection
                </label>
                {dsError && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-100 rounded text-xs text-red-700">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />{dsError}
                    </div>
                )}
                <div className="flex gap-2">
                    <select
                        value={selectedDs}
                        onChange={e => handleDsChange(e.target.value)}
                        disabled={dsLoading}
                        className="flex-1 text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">{dsLoading ? 'Loading…' : '— Select a connection —'}</option>
                        {dataSources.map(ds => (
                            <option key={ds.id} value={ds.id}>
                                {ds.name} ({DB_TYPE_LABELS[ds.type] || `Type ${ds.type}`})
                            </option>
                        ))}
                    </select>
                    <button type="button" onClick={loadDs} disabled={dsLoading}
                        className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 transition-colors shrink-0"
                        title="Refresh connections">
                        <RefreshCw className={`h-3.5 w-3.5 ${dsLoading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {schemaLoading && (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-500">
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Loading table schema…
                </div>
            )}
            {schemaError && (
                <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-100 rounded text-xs text-red-700">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />{schemaError}
                </div>
            )}

            {/* ── Table list with PD mapping ─────────────────────────── */}
            {!schemaLoading && tables.length > 0 && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                            <Table2 className="h-3.5 w-3.5 text-purple-500" />
                            Tables &amp; PD Mapping
                        </label>
                        <div className="flex items-center gap-2 text-[10px]">
                            <span className="text-gray-400">{includedTables.length}/{tables.length} selected</span>
                            {pdTables.length > 0 && (
                                <span className="font-semibold text-red-600">· {pdTables.length} PD</span>
                            )}
                        </div>
                    </div>

                    <div className="space-y-1.5 max-h-[440px] overflow-y-auto pr-0.5">
                        {tables.map(table => (
                            <TableAccordion
                                key={table.name}
                                table={table}
                                included={tableConfig[table.name]?.included !== false}
                                colMappings={tableConfig[table.name]?.columns || {}}
                                onToggle={inc => handleToggle(table.name, inc)}
                                onColChange={(col, m) => handleColChange(table.name, col, m)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
